import { useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { toast } from "sonner"
import { api } from "@/services/api"
import { useAuth } from "@/contexts/auth-context"

interface MarketplaceOption {
  id: number
  uuid: string
  name: string
  description: string
  status: number
  kind: string
}

export default function OrganizationSetupPage() {
  const navigate = useNavigate()
  const {} = useAuth()

  // Form states
  const [organizationName, setOrganizationName] = useState("")
  const [selectedMarketplace, setSelectedMarketplace] = useState("")
  const [marketplaceOptions, setMarketplaceOptions] = useState<MarketplaceOption[]>([])
  const [marketplaceLoading, setMarketplaceLoading] = useState(false)
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const submitInProgress = useRef(false)

  // Stripe Checkout session ID is appended to the URL by Stripe after the
  // user finishes payment. The org-creation request includes this so the
  // backend can link the prepaid Stripe customer/subscription to the new org
  // instead of creating a fresh pair (and double-billing in production).

  useEffect(() => {
    // Check if user has required tokens
    // After password setup, reset_password_token is removed, so just check for authToken
    const authToken = localStorage.getItem('authToken')

    if (!authToken) {
      console.warn('Missing auth token - redirecting to home')
      navigate('/')
      return
    }

    // Card-less free-tier onboarding: no Stripe Checkout session is required.
    // The org is created on the free tier; a card is only collected later when
    // it crosses the free-trial ceiling.

    // Load marketplace options (don't block rendering if this fails)
    loadMarketplaceOptions()
  }, [navigate])

  const loadMarketplaceOptions = async () => {
    try {
      setMarketplaceLoading(true)
      const response = await api.get('/marketplace')
      
      // Ensure we have a valid array, sorted alphabetically by name
      const options = Array.isArray(response.data) ? response.data : []
      options.sort((a: MarketplaceOption, b: MarketplaceOption) => (a.name || '').localeCompare(b.name || ''))
      setMarketplaceOptions(options)
    } catch (error) {
      console.error('Error loading marketplace options:', error)
      // Don't show error toast - it's optional
      // Just set empty array and continue
      setMarketplaceOptions([])
    } finally {
      setMarketplaceLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (submitInProgress.current) return

    if (!organizationName.trim()) {
      toast.error('Please enter an organization name')
      return
    }

    if (!termsAccepted) {
      toast.error('Please accept the Terms and Conditions')
      return
    }

    submitInProgress.current = true
    setIsSubmitting(true)

    try {
      // Create organization on the free tier (no checkout_session_id → backend
      // creates a free subscription, no Stripe).
      const requestData = {
        name: organizationName.trim(),
        marketplace_id: selectedMarketplace && selectedMarketplace !== "none" ? parseInt(selectedMarketplace) : null,
      }

      const authToken = localStorage.getItem('authToken')
      const response = await api.post('/organizations', requestData, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      })

      const orgData = response.data

      // Save organization data
      localStorage.setItem('activeOrganization', orgData.slug)
      localStorage.setItem('organizationApiKey', orgData.api_key)

      toast.success('Organization created successfully!')

      // Redirect to the organization
      navigate(`/${orgData.slug}/optimizer`)

    } catch (error: any) {
      console.error('Error creating organization:', error)
      
      if (error.response?.data?.detail) {
        const errorMessage = Array.isArray(error.response.data.detail.errors) 
          ? error.response.data.detail.errors.join(', ')
          : error.response.data.detail
        toast.error(`Error creating organization: ${errorMessage}`)
      } else {
        toast.error('Error creating organization. Please try again.')
      }
    } finally {
      submitInProgress.current = false
      setIsSubmitting(false)
    }
  }

  return (
    <div className="container relative h-screen flex-col items-center justify-center md:grid lg:max-w-none lg:grid-cols-2 lg:px-0">
      <div className="relative hidden h-full flex-col bg-muted p-10 text-white lg:flex dark:border-r">
        <div className="absolute inset-0 bg-slate-700" />
        <div className="relative z-20 flex items-center text-lg font-medium">
          <img src="/logo_white.png" alt="Glaium Logo" className="h-8 w-auto" />
        </div>
      </div>
      <div className="flex h-full items-center p-4 lg:p-8 bg-muted/40">
        <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]">
          <div className="flex flex-col space-y-2 text-center">
            <h1 className="text-2xl font-semibold tracking-tight">Create Organization</h1>
            <p className="text-sm text-muted-foreground">
              Set up your organization to get started with Glaium
            </p>
          </div>
          
          <div className="grid gap-6">
            <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="organizationName">Organization Name</Label>
              <Input
                id="organizationName"
                type="text"
                value={organizationName}
                onChange={(e) => setOrganizationName(e.target.value)}
                placeholder="Enter your organization name"
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="businessModel">Business Model (Optional)</Label>
              {marketplaceLoading ? (
                <div className="text-sm text-muted-foreground">Loading business models...</div>
              ) : (
                <Select 
                  value={selectedMarketplace} 
                  onValueChange={setSelectedMarketplace}
                  disabled={isSubmitting}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a business model" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No business model</SelectItem>
                    {Array.isArray(marketplaceOptions) && marketplaceOptions.map((option) => {
                      // Ensure we have valid data
                      if (!option?.id || !option?.name) return null
                      return (
                        <SelectItem key={option.id} value={option.id.toString()}>
                          {option.name}
                        </SelectItem>
                      )
                    })}
                  </SelectContent>
                </Select>
              )}
              <p className="text-sm text-muted-foreground">
                Choose a business model template to help configure your organization
              </p>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="terms"
                checked={termsAccepted}
                onCheckedChange={(checked) => setTermsAccepted(checked as boolean)}
                disabled={isSubmitting}
              />
              <Label htmlFor="terms" className="text-sm">
                I agree to the{" "}
                <a
                  href="https://glaium.io/legal/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  Terms and Conditions
                </a>
              </Label>
            </div>

            <Button 
              type="submit" 
              className="w-full" 
              disabled={isSubmitting}
            >
              {isSubmitting ? "Creating Organization..." : "Create Organization"}
            </Button>
          </form>
          </div>
        </div>
      </div>
    </div>
  )
}